import React, { useState, useEffect } from 'react';
import {
    View,
    Text,
    ScrollView,
    TouchableOpacity,
    StyleSheet,
    Platform,
    RefreshControl,
    ActivityIndicator,
    TextInput,
    Alert,
    Modal,
    Dimensions
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import AdminLayout from '../components/layout/AdminLayout';
import { getFeedbackList, getFeedbackStats, updateFeedback } from '../api/feedback';
import { getColor, getSpacing, getBorderRadius, getShadow } from '../theme/adminTheme';

const FeedbackManagementScreen = () => {
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [activeTab, setActiveTab] = useState('list'); // 'list' or 'report'
    const [feedback, setFeedback] = useState([]);
    const [stats, setStats] = useState(null);
    const [selectedFeedback, setSelectedFeedback] = useState(null);
    const [adminNotes, setAdminNotes] = useState('');
    const [status, setStatus] = useState('');
    const [isUpdating, setIsUpdating] = useState(false);

    // Filters
    const [statusFilter, setStatusFilter] = useState('');
    const [typeFilter, setTypeFilter] = useState('');

    useEffect(() => {
        fetchData();
    }, [statusFilter, typeFilter]);

    const fetchData = async () => {
        try {
            setLoading(true);
            const listData = await getFeedbackList({ status: statusFilter, type: typeFilter });
            const statsData = await getFeedbackStats();

            if (listData.success) setFeedback(listData.data);
            if (statsData.success) setStats(statsData.data);
        } catch (error) {
            console.error('Error fetching feedback data:', error);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    const onRefresh = () => {
        setRefreshing(true);
        fetchData();
    };

    const handleUpdate = async () => {
        if (!selectedFeedback) return;
        try {
            setIsUpdating(true);
            const result = await updateFeedback(selectedFeedback.id, {
                status,
                adminNotes
            });
            if (result.success) {
                Alert.alert('Success', 'Feedback updated successfully');
                setSelectedFeedback(null);
                fetchData();
            }
        } catch (error) {
            Alert.alert('Error', 'Failed to update feedback');
        } finally {
            setIsUpdating(false);
        }
    };

    const handlePrint = () => {
        if (Platform.OS === 'web') {
            window.print();
        } else {
            Alert.alert('Note', 'Printing is currently only supported in the web interface of the dashboard.');
        }
    };

    const renderList = () => (
        <View style={styles.listContainer}>
            <View style={styles.filterBar}>
                <View style={styles.filterGroup}>
                    <Text style={styles.filterLabel}>Status:</Text>
                    <View style={styles.chipRow}>
                        {['', 'pending', 'reviewed', 'resolved'].map(f => (
                            <TouchableOpacity
                                key={f}
                                style={[styles.miniChip, statusFilter === f && styles.activeChip]}
                                onPress={() => setStatusFilter(f)}
                            >
                                <Text style={[styles.miniChipText, statusFilter === f && styles.activeChipText]}>
                                    {f === '' ? 'All' : f.toUpperCase()}
                                </Text>
                            </TouchableOpacity>
                        ))}
                    </View>
                </View>
            </View>

            {feedback.length === 0 ? (
                <View style={styles.emptyContainer}>
                    <Text style={styles.emptyText}>No feedback found matching the criteria.</Text>
                </View>
            ) : (
                feedback.map((item) => (
                    <TouchableOpacity
                        key={item.id}
                        style={[styles.feedbackCard, getShadow('sm')]}
                        onPress={() => {
                            setSelectedFeedback(item);
                            setStatus(item.status);
                            setAdminNotes(item.adminNotes || '');
                        }}
                    >
                        <View style={styles.cardHeader}>
                            <View style={[styles.statusBadge, item.status === 'pending' ? styles.statusPending : styles.statusResolved]}>
                                <Text style={styles.statusBadgeText}>{item.status.toUpperCase()}</Text>
                            </View>
                            <Text style={styles.feedbackDate}>{new Date(item.createdAt).toLocaleDateString()}</Text>
                        </View>
                        <Text style={styles.feedbackSubject}>{item.subject}</Text>
                        <Text style={styles.feedbackType}>{item.type.toUpperCase()}</Text>
                        <Text style={styles.feedbackUser}>From: {item.user?.name || 'Unknown'}</Text>
                    </TouchableOpacity>
                ))
            )}
        </View>
    );

    const renderStats = () => {
        if (!stats) return null;
        return (
            <View style={styles.reportContainer} className="printable-report">
                <View style={styles.reportHeader}>
                    <Text style={styles.reportTitle}>Feedback Analysis Report</Text>
                    <Text style={styles.reportSubtitle}>Generated on {new Date().toLocaleString()}</Text>
                </View>

                <View style={styles.statsGrid}>
                    <View style={styles.statsCard}>
                        <Text style={styles.statsLabel}>Total Submissions</Text>
                        <Text style={styles.statsValue}>
                            {stats.statusDistribution?.reduce((acc, curr) => acc + parseInt(curr.count), 0) || 0}
                        </Text>
                    </View>
                    <View style={styles.statsCard}>
                        <Text style={styles.statsLabel}>Pending Review</Text>
                        <Text style={[styles.statsValue, { color: getColor('danger.600') }]}>
                            {stats.statusDistribution?.find(s => s.status === 'pending')?.count || 0}
                        </Text>
                    </View>
                </View>

                <View style={styles.chartSection}>
                    <Text style={styles.sectionTitle}>Distribution via Feedback Type</Text>
                    {stats.typeDistribution?.map(type => (
                        <View key={type.type} style={styles.barContainer}>
                            <View style={styles.barLabelRow}>
                                <Text style={styles.barLabel}>{type.type.toUpperCase()}</Text>
                                <Text style={styles.barValue}>{type.count}</Text>
                            </View>
                            <View style={styles.barBg}>
                                <View style={[styles.barFill, { width: `${(type.count / feedback.length) * 100}%` }]} />
                            </View>
                        </View>
                    ))}
                </View>

                <TouchableOpacity style={styles.printButton} onPress={handlePrint} className="no-print">
                    <Ionicons name="print" size={20} color="#FFF" />
                    <Text style={styles.printButtonText}>Print Analysis Report</Text>
                </TouchableOpacity>
            </View>
        );
    };

    return (
        <AdminLayout>
            <ScrollView
                style={styles.container}
                refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} />}
            >
                <View style={styles.header}>
                    <Text style={styles.headerTitle}>User Feedback Management</Text>
                    <Text style={styles.headerSubtitle}>Review and analyze user input.</Text>
                </View>

                <View style={styles.tabsRow}>
                    <TouchableOpacity
                        style={[styles.tab, activeTab === 'list' && styles.activeTab]}
                        onPress={() => setActiveTab('list')}
                    >
                        <Text style={[styles.tabText, activeTab === 'list' && styles.activeTabText]}>Submissions</Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                        style={[styles.tab, activeTab === 'report' && styles.activeTab]}
                        onPress={() => setActiveTab('report')}
                    >
                        <Text style={[styles.tabText, activeTab === 'report' && styles.activeTabText]}>Report & Analysis</Text>
                    </TouchableOpacity>
                </View>

                {loading && !refreshing ? (
                    <ActivityIndicator size="large" color={getColor('primary.600')} style={{ marginTop: 40 }} />
                ) : (
                    activeTab === 'list' ? renderList() : renderStats()
                )}
            </ScrollView>

            {/* Detail Modal */}
            <Modal visible={!!selectedFeedback} animationType="slide" transparent={true}>
                <View style={styles.modalOverlay}>
                    <View style={styles.modalContent}>
                        <View style={styles.modalHeader}>
                            <Text style={styles.modalTitle}>Feedback Details</Text>
                            <TouchableOpacity onPress={() => setSelectedFeedback(null)}>
                                <Ionicons name="close" size={24} color={getColor('gray.600')} />
                            </TouchableOpacity>
                        </View>

                        {selectedFeedback && (
                            <ScrollView style={styles.modalBody}>
                                <View style={styles.metaRow}>
                                    <Text style={styles.metaLabel}>USER:</Text>
                                    <Text style={styles.metaValue}>{selectedFeedback.user?.name || 'Anonymous'}</Text>
                                </View>
                                <View style={styles.metaRow}>
                                    <Text style={styles.metaLabel}>TYPE:</Text>
                                    <Text style={styles.metaValue}>{selectedFeedback.type.toUpperCase()}</Text>
                                </View>

                                <View style={styles.contentBox}>
                                    <Text style={styles.contentSubject}>{selectedFeedback.subject}</Text>
                                    <Text style={styles.contentText}>{selectedFeedback.content}</Text>
                                </View>

                                <View style={styles.adminSection}>
                                    <Text style={styles.sectionTitle}>Admin Response</Text>
                                    <Text style={styles.inputLabel}>Status</Text>
                                    <View style={styles.chipRow}>
                                        {['pending', 'reviewed', 'resolved', 'ignored'].map(s => (
                                            <TouchableOpacity
                                                key={s}
                                                style={[styles.miniChip, status === s && styles.activeChip]}
                                                onPress={() => setStatus(s)}
                                            >
                                                <Text style={[styles.miniChipText, status === s && styles.activeChipText]}>{s.toUpperCase()}</Text>
                                            </TouchableOpacity>
                                        ))}
                                    </View>

                                    <Text style={[styles.inputLabel, { marginTop: 15 }]}>Admin Notes (Internal)</Text>
                                    <TextInput
                                        style={styles.textArea}
                                        multiline
                                        numberOfLines={4}
                                        value={adminNotes}
                                        onChangeText={setAdminNotes}
                                        placeholder="Enter notes about this feedback..."
                                    />

                                    <TouchableOpacity
                                        style={styles.updateButton}
                                        onPress={handleUpdate}
                                        disabled={isUpdating}
                                    >
                                        {isUpdating ? (
                                            <ActivityIndicator color="#FFF" size="small" />
                                        ) : (
                                            <Text style={styles.updateButtonText}>Update Feedback Status</Text>
                                        )}
                                    </TouchableOpacity>
                                </View>
                            </ScrollView>
                        )}
                    </View>
                </View>
            </Modal>

            {Platform.OS === 'web' && (
                <style dangerouslySetInnerHTML={{
                    __html: `
          @media print {
            .no-print, nav, .header, .tabsRow { display: none !important; }
            .contentSurface, .container, .printable-report { 
              background: white !important; 
              padding: 0 !important; 
              margin: 0 !important;
            }
            .printable-report { display: block !important; }
          }
        `}} />
            )}
        </AdminLayout>
    );
};

const styles = StyleSheet.create({
    container: { flex: 1, backgroundColor: getColor('gray.50'), padding: getSpacing('lg') },
    header: { marginBottom: getSpacing('xl') },
    headerTitle: { fontSize: 24, fontWeight: '700', color: getColor('gray.900') },
    headerSubtitle: { fontSize: 14, color: getColor('gray.600'), marginTop: 4 },
    tabsRow: { flexDirection: 'row', backgroundColor: '#FFF', borderRadius: 12, marginBottom: 20, padding: 4 },
    tab: { flex: 1, paddingVertical: 10, alignItems: 'center', borderRadius: 10 },
    activeTab: { backgroundColor: getColor('primary.50') },
    tabText: { fontWeight: '600', color: getColor('gray.500') },
    activeTabText: { color: getColor('primary.700') },
    filterBar: { marginBottom: 15 },
    filterGroup: { marginBottom: 10 },
    filterLabel: { fontSize: 13, fontWeight: '600', color: getColor('gray.700'), marginBottom: 8 },
    chipRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
    miniChip: { backgroundColor: '#F1F5F9', paddingHorizontal: 12, paddingVertical: 6, borderRadius: 8, borderWidth: 1, borderColor: '#E2E8F0' },
    activeChip: { backgroundColor: getColor('primary.500'), borderColor: getColor('primary.500') },
    miniChipText: { fontSize: 11, fontWeight: '700', color: getColor('gray.500') },
    activeChipText: { color: '#FFF' },
    feedbackCard: { backgroundColor: '#FFF', borderRadius: 12, padding: 16, marginBottom: 12, borderLeftWidth: 4, borderLeftColor: getColor('primary.500') },
    cardHeader: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 10 },
    statusBadge: { paddingHorizontal: 8, paddingVertical: 2, borderRadius: 6 },
    statusPending: { backgroundColor: '#FEE2E2' },
    statusResolved: { backgroundColor: '#DCFCE7' },
    statusBadgeText: { fontSize: 10, fontWeight: '700', color: '#111' },
    feedbackDate: { fontSize: 12, color: getColor('gray.400') },
    feedbackSubject: { fontSize: 16, fontWeight: '700', color: getColor('gray.800'), marginBottom: 4 },
    feedbackType: { fontSize: 11, fontWeight: '600', color: getColor('primary.600'), marginBottom: 8 },
    feedbackUser: { fontSize: 12, color: getColor('gray.500') },
    modalOverlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.5)', justifyContent: 'flex-end' },
    modalContent: { backgroundColor: '#FFF', borderTopLeftRadius: 24, borderTopRightRadius: 24, height: '85%', padding: 24 },
    modalHeader: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 20 },
    modalTitle: { fontSize: 20, fontWeight: '700', color: getColor('gray.900') },
    metaRow: { flexDirection: 'row', marginBottom: 8 },
    metaLabel: { width: 60, fontSize: 12, fontWeight: '700', color: getColor('gray.400') },
    metaValue: { fontSize: 13, color: getColor('gray.800') },
    contentBox: { backgroundColor: '#F8FAFC', padding: 16, borderRadius: 12, marginVertical: 20 },
    contentSubject: { fontSize: 15, fontWeight: '700', color: getColor('gray.900'), marginBottom: 8 },
    contentText: { fontSize: 14, color: getColor('gray.700'), lineHeight: 20 },
    adminSection: { marginTop: 10 },
    sectionTitle: { fontSize: 16, fontWeight: '700', color: getColor('gray.900'), marginBottom: 15 },
    inputLabel: { fontSize: 13, fontWeight: '600', color: getColor('gray.600'), marginBottom: 10 },
    textArea: { backgroundColor: '#F1F5F9', borderRadius: 12, padding: 12, textAlignVertical: 'top', minHeight: 100, fontSize: 14 },
    updateButton: { backgroundColor: getColor('primary.600'), paddingVertical: 14, borderRadius: 12, alignItems: 'center', marginTop: 20 },
    updateButtonText: { color: '#FFF', fontWeight: '700', fontSize: 15 },
    reportContainer: { padding: 20, backgroundColor: '#FFF', borderRadius: 16 },
    reportHeader: { borderBottomWidth: 1, borderBottomColor: '#F1F5F9', paddingBottom: 15, marginBottom: 20 },
    reportTitle: { fontSize: 22, fontWeight: '700', color: getColor('gray.900') },
    reportSubtitle: { fontSize: 12, color: getColor('gray.400'), marginTop: 4 },
    statsGrid: { flexDirection: 'row', gap: 15, marginBottom: 30 },
    statsCard: { flex: 1, backgroundColor: '#F8FAFC', padding: 15, borderRadius: 12, borderWidth: 1, borderColor: '#F1F5F9' },
    statsLabel: { fontSize: 12, color: getColor('gray.500'), marginBottom: 6 },
    statsValue: { fontSize: 24, fontWeight: '800', color: getColor('primary.700') },
    chartSection: { marginBottom: 30 },
    barContainer: { marginBottom: 15 },
    barLabelRow: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 6 },
    barLabel: { fontSize: 12, fontWeight: '700', color: getColor('gray.600') },
    barValue: { fontSize: 12, fontWeight: '700', color: getColor('primary.700') },
    barBg: { height: 8, backgroundColor: '#F1F5F9', borderRadius: 4, overflow: 'hidden' },
    barFill: { height: '100%', backgroundColor: getColor('primary.500') },
    printButton: { flexDirection: 'row', backgroundColor: getColor('gray.800'), padding: 14, borderRadius: 10, justifyContent: 'center', alignItems: 'center', gap: 8 },
    printButtonText: { color: '#FFF', fontWeight: '700' },
    emptyContainer: { padding: 40, alignItems: 'center' },
    emptyText: { color: getColor('gray.400') }
});

export default FeedbackManagementScreen;
